import { z } from 'zod';

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export interface JsonObject {
  [key: string]: JsonValue;
}

export const JSON_VALUE_LIMITS = Object.freeze({
  maxDepth: 64,
  maxNodes: 100000,
  maxCollectionEntries: 100000,
  maxStringLength: 1000000,
  maxKeyLength: 500,
});

const forbiddenObjectKeys = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

export interface JsonValueValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Iterative validation keeps deeply nested external input away from recursive
 * JavaScript call stacks. It is shared by Agent, Project, Runtime and
 * component configuration boundaries.
 */
export function validateJsonValue(value: unknown): JsonValueValidationResult {
  const pending: Array<{ value: unknown; depth: number }> = [{
    value,
    depth: 0,
  }];
  let visitedNodes = 0;

  while (pending.length > 0) {
    const current = pending.pop()!;
    visitedNodes += 1;
    if (visitedNodes > JSON_VALUE_LIMITS.maxNodes) {
      return {
        valid: false,
        message: `JSON 节点数量不能超过 ${JSON_VALUE_LIMITS.maxNodes}`,
      };
    }
    if (current.depth > JSON_VALUE_LIMITS.maxDepth) {
      return {
        valid: false,
        message: `JSON 嵌套深度不能超过 ${JSON_VALUE_LIMITS.maxDepth}`,
      };
    }

    const item = current.value;
    if (
      item === null
      || typeof item === 'boolean'
      || (typeof item === 'number' && Number.isFinite(item))
    ) {
      continue;
    }
    if (typeof item === 'string') {
      if (item.length > JSON_VALUE_LIMITS.maxStringLength) {
        return {
          valid: false,
          message: `JSON 字符串长度不能超过 ${JSON_VALUE_LIMITS.maxStringLength}`,
        };
      }
      continue;
    }
    if (Array.isArray(item)) {
      if (item.length > JSON_VALUE_LIMITS.maxCollectionEntries) {
        return {
          valid: false,
          message: `JSON 数组长度不能超过 ${JSON_VALUE_LIMITS.maxCollectionEntries}`,
        };
      }
      for (let index = item.length - 1; index >= 0; index -= 1) {
        pending.push({
          value: item[index],
          depth: current.depth + 1,
        });
      }
      continue;
    }
    if (typeof item !== 'object') {
      return {
        valid: false,
        message: '值必须是可序列化的 JSON',
      };
    }

    const prototype = Object.getPrototypeOf(item);
    if (prototype !== Object.prototype && prototype !== null) {
      return {
        valid: false,
        message: 'JSON 对象必须是普通对象',
      };
    }
    const entries = Object.entries(item);
    if (entries.length > JSON_VALUE_LIMITS.maxCollectionEntries) {
      return {
        valid: false,
        message: `JSON 对象字段数量不能超过 ${JSON_VALUE_LIMITS.maxCollectionEntries}`,
      };
    }
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const [key, child] = entries[index]!;
      if (key.length > JSON_VALUE_LIMITS.maxKeyLength) {
        return {
          valid: false,
          message: `JSON 字段名长度不能超过 ${JSON_VALUE_LIMITS.maxKeyLength}`,
        };
      }
      if (forbiddenObjectKeys.has(key)) {
        return {
          valid: false,
          message: `JSON 包含禁止字段：${key}`,
        };
      }
      pending.push({
        value: child,
        depth: current.depth + 1,
      });
    }
  }

  return { valid: true };
}

export const jsonPrimitiveSchema = z.union([
  z.string().max(JSON_VALUE_LIMITS.maxStringLength),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);

export const jsonValueSchema = z.custom<JsonValue>(
  value => validateJsonValue(value).valid,
  { message: '值不是安全且有界的 JSON' },
);

export const jsonObjectSchema = z.custom<JsonObject>(
  value => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
    && validateJsonValue(value).valid
  ),
  { message: '值不是安全且有界的 JSON 对象' },
);
